// Die Blockfigur als Three.js-Objekt: ein Quader je Eintrag im Bauplan aus `shared`, gruppiert zu
// Gelenken an Schulter und Hüfte. Ursprung ist die Fußpunkt-Mitte, der Blick geht bei yaw 0 nach
// −z – dieselbe Konvention wie Kamera, Simulation und Tiermodelle.
//
// Dasselbe Modell zeigt drei Dinge: den eigenen Avatar in der Schulterkamera, die Figur im Spiegel
// und ab task-016 die Mitspieler. In den Three.js-Objekten liegt kein Spielzustand
// (Architektur-Regel 1); Farben und Pendelwinkel werden von außen gesetzt.
//
// Je Farbbereich gibt es genau ein Material. Eine neue Farbe kostet damit einen Wert, keinen
// Neubau: `setFarben` färbt vier Materialien um, und alle Quader ziehen mit.

import { BoxGeometry, Color, Group, Mesh, MeshLambertMaterial } from 'three';
import { AVATAR_PENDEL, avatarBauplan, type AvatarBereich, type AvatarColors, type AvatarTeil } from '@bauernhof/shared';

/** Augen und Mund sind nicht wählbar: ein festes Dunkelbraun, das auf jeder Hautfarbe zu sehen ist. */
export const GESICHT_FARBE = '#2b1d16';

export interface AvatarModel {
  /** Die Figur; Position und Drehung setzt der Aufrufer je Bild aus dem Spielzustand. */
  object: Group;
  /** Färbt die vier Bereiche um; sofort sichtbar, auch während der Spiegel offen ist. */
  setFarben(farben: AvatarColors): void;
  /** Pendelwinkel der Glieder im Bogenmaß (aus `createGang`); 0 heißt stehen. */
  setSwing(swing: number): void;
  /** Gibt Geometrien und Materialien dieser Figur frei. */
  dispose(): void;
}

export function createAvatarModel(farben: AvatarColors): AvatarModel {
  const plan = avatarBauplan();
  const geometrien: BoxGeometry[] = [];
  const materialien = new Map<AvatarBereich, MeshLambertMaterial>();
  const gelenke = new Map<AvatarTeil, Group>();

  const materialFuer = (bereich: AvatarBereich): MeshLambertMaterial => {
    const vorhanden = materialien.get(bereich);
    if (vorhanden !== undefined) return vorhanden;
    const material = new MeshLambertMaterial({ color: new Color(bereich === 'gesicht' ? GESICHT_FARBE : farben[bereich]) });
    materialien.set(bereich, material);
    return material;
  };

  const object = new Group();
  object.name = 'avatar';

  for (const quader of plan.teile) {
    const [sx, sy, sz] = quader.size;
    const geometry = new BoxGeometry(sx, sy, sz);
    geometrien.push(geometry);
    const mesh = new Mesh(geometry, materialFuer(quader.bereich));
    mesh.position.set(...quader.center);
    mesh.name = `${quader.teil} ${quader.bereich}`;

    if (quader.pivot === undefined) {
      object.add(mesh);
      continue;
    }
    // Alle Quader eines Glieds teilen sich ein Gelenk; um dessen Ursprung pendelt es beim Gehen.
    let gelenk = gelenke.get(quader.teil);
    if (gelenk === undefined) {
      gelenk = new Group();
      gelenk.name = quader.teil;
      gelenk.position.set(...quader.pivot);
      object.add(gelenk);
      gelenke.set(quader.teil, gelenk);
    }
    gelenk.add(mesh);
  }

  return {
    object,

    setFarben(neue) {
      for (const [bereich, material] of materialien) {
        if (bereich === 'gesicht') continue;
        material.color.set(neue[bereich]);
      }
    },

    setSwing(swing) {
      for (const [teil, gelenk] of gelenke) gelenk.rotation.x = AVATAR_PENDEL[teil] * swing;
    },

    dispose() {
      for (const geometry of geometrien) geometry.dispose();
      geometrien.length = 0;
      for (const material of materialien.values()) material.dispose();
      materialien.clear();
      object.removeFromParent();
    },
  };
}
